import { Injectable } from '@angular/core';
import { AdminChangesLog } from '../interfaces/invoice-interfaces';

@Injectable({
  providedIn: 'root'
})
export class UtilsService {
  constructor() { }

  getObjectDifferences(obj1: any, obj2: any, ignoreFields: string[] = []): any {
    const isObj = (v: any) => v !== null && v !== undefined && typeof v === 'object' && !Array.isArray(v);

    const eq = (a: any, b: any) => {
      // Null
      if (a == null && b == null) return true;

      // NaN
      if (typeof a === 'number' && typeof b === 'number' && Number.isNaN(a) && Number.isNaN(b)) return true;

      // Dates
      if ((typeof a === 'string' || a instanceof Date) && (typeof b === 'string' || b instanceof Date)) {
        const da = new Date(a);
        const db = new Date(b);
        if (!isNaN(da.getTime()) && !isNaN(db.getTime())) {
          return da.getTime() === db.getTime();
        }
      }

      return a === b;
    };

    if (obj1 == null && obj2 == null) return undefined;
    if (obj1 == null || obj2 == null) return [obj1, obj2];

    if (!isObj(obj1) || !isObj(obj2)) {
      return eq(obj1, obj2) ? undefined : [obj1, obj2];
    }

    const diffs: any = {};
    const uniqueKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

    for (const key of uniqueKeys) {
      if (ignoreFields.includes(key)) continue;

      const v1 = obj1[key];
      const v2 = obj2[key];

      if (Array.isArray(v1) && Array.isArray(v2)) {
        const max = Math.max(v1.length, v2.length);
        const arrDiff: any = {};
        for (let i = 0; i < max; i++) {
          const nd = this.getObjectDifferences(v1[i], v2[i], ignoreFields);
          if (nd !== undefined) arrDiff[i] = nd;
        }
        if (Object.keys(arrDiff).length) diffs[key] = arrDiff;
        continue;
      }

      if (isObj(v1) && isObj(v2)) {
        const nested = this.getObjectDifferences(v1, v2, ignoreFields);
        if (nested !== undefined) diffs[key] = nested;
        continue;
      }

      if (!eq(v1, v2)) {
        diffs[key] = [v1, v2];
      }
    }

    return Object.keys(diffs).length === 0 ? undefined : diffs;
  }

  // use with getObjectDifferences method
  getAdminChangesLogData(difference: any, message?: string, exprMap?: Record<string, string>): AdminChangesLog {
    const changes_summary: Record<string, { old: any; new: any }> = {};

    if (!difference) {
      return { message, changes_summary: {} };
    }

    const isIndex = (k: string) => /^\d+$/.test(k);

    const parseAdditionalPath = (path: string) => {
      const m = path.match(/^(?:pods|pdrs)\[(\d+)\]\.additional_note_fields\.([A-Za-z0-9_]+)$/);

      if (!m) return null;
      return { index: Number(m[1]), field: m[2] };
    };

    const maybeAttachExpr = (path: string) => {
      const parsed = parseAdditionalPath(path);
      if (!parsed || !exprMap) return;
      const key = `${parsed.field}#${parsed.index}`;
      const expr = exprMap[key];
      if (expr && changes_summary[path]) {
        // append expression to new value
        const currentNew = changes_summary[path].new;
        changes_summary[path].new = `${currentNew} (${expr})`;
      }
    };

    const walk = (node: any, path: string) => {
      // [old, new]
      if (Array.isArray(node) && node.length === 2) {
        changes_summary[path] = { old: node[0], new: node[1] };
        maybeAttachExpr(path);
        return;
      }

      // Nested object
      if (node && typeof node === 'object') {
        for (const key of Object.keys(node)) {
          const nextPath = path
            ? (isIndex(key) ? `${path}[${key}]` : `${path}.${key}`)
            : (isIndex(key) ? `[${key}]` : key);
          walk(node[key], nextPath);
        }
      }
    };

    walk(difference, '');

    return {
      message,
      changes_summary,
    };
  }

  numberOrNull(v: unknown): number | null {
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v === 'string') {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }

  getMultiLineLabels(text: string, maxLineLength: number): string[] {
    const words = text.split(" ");
    const lines: string[] = [];
    let currentLine = "";

    words.forEach(word => {
      if ((currentLine + (currentLine ? " " : "") + word).length <= maxLineLength) {
        currentLine += (currentLine ? " " : "") + word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines;
  }
}